import { newUuid } from '@overlayqa/shared';
import React, {useCallback, useEffect, useRef, useState, type DragEvent, type ReactNode} from 'react';
import {toast} from 'sonner';
import {SelectDropdown} from '../../shared/SelectDropdown';
import {ConfirmDialog} from '../../shared/ConfirmDialog';
import {IssueBoardLane} from './IssueBoardLane';
import {useQueryClient} from '@tanstack/react-query';
import {ISSUE_STATUS_OPTIONS} from '../../../lib/issueTypeOptions';
import {useInlineIssueUpdate} from '../../../hooks/useInlineIssueUpdate';
import {ISSUE_MUTATION_INVALIDATE_KEYS} from '../../../hooks/issueMutationInvalidation';
import {useAuth} from '../../../lib/auth';
import {ticketsApi} from '../../../lib/api';
import {capture} from '../../../lib/analytics';
import {type Ticket, type TicketStatus, toUserFacingMessage} from '@overlayqa/shared';
import type {IssueBoardPreferences} from './useIssueBoardPreferences';
import './issue-board.css';
type Issue = Ticket;
type Column = {id:string;label:string;status:TicketStatus;cards:string[]};
type Target = {columnId: string; beforeId?: string};
/** The settle's keyframes in issue-board.css; the card's own animations (an image loader) must not end it. */
const SETTLE_ANIMATION = 'oq-issue-board-settle';


export function IssueBoard({issues, allIssues, renderIssue, readOnly, projectId, preferences}: {
  issues: Issue[]; allIssues: Issue[];
  renderIssue: (issue: Issue, onMove: (issue: Issue) => void, busy: boolean) => ReactNode;
  readOnly: boolean; projectId: string; preferences: IssueBoardPreferences;
}) {
  const {visible: visibleColumns, setVisible, place} = preferences;
  // List queries can briefly carry previous-project data. Never display or move it here.
  const projectIssues = allIssues.filter(issue => issue.projectId === projectId);
  const projectIds = new Set(projectIssues.map(issue => issue.id));
  const order = [...new Set([...preferences.order.filter(id => projectIds.has(id)), ...projectIds])];
  const positions = new Map(order.map((id, index) => [id, index]));
  const columns:Column[] = ISSUE_STATUS_OPTIONS.map(({value,label}) => ({id:value,status:value as TicketStatus,label,
    cards:issues.filter(issue => issue.projectId === projectId && issue.status === value).sort((a,b) => (positions.get(a.id) ?? 0)-(positions.get(b.id) ?? 0)).map(issue=>issue.id)}));
  const currentIssues = useRef(projectIssues); currentIssues.current = projectIssues;
  const toastId = useRef(`issue-board-${projectId}-${newUuid()}`).current;
  // One fresh toast id per move. sonner keeps a dismissed id's removal flag
  // through an update, so a toast minted under the same id inside the previous
  // one's 200 ms exit vanished with it; the awaited refetch used to sit between
  // the two (production, 2026-10-23: the Undo toast was gone before a click).
  const activeToast = useRef<string | null>(null);
  const moveSerial = useRef(0);
  const mounted = useRef(true);
  useEffect(() => {mounted.current=true; return () => {mounted.current=false;if(activeToast.current)toast.dismiss(activeToast.current);};}, []);
  const queryClient = useQueryClient();
  const {getToken} = useAuth();
  const update = useInlineIssueUpdate();
  const pending = useRef(false);
  const [busy, setBusy] = useState(false);
  // The one card settling into Verified, for the length of its swell.
  const [settling, setSettling] = useState<string | null>(null);
  // A Status filter can hide the card on arrival; with no swell to end, it would settle whenever the filter changes.
  useEffect(() => { if (settling && !issues.some(issue => issue.id === settling)) setSettling(null); }, [issues, settling]);
  const refresh = useCallback(async () => {
    await Promise.all(ISSUE_MUTATION_INVALIDATE_KEYS.map(queryKey => queryClient.invalidateQueries({queryKey})));
  }, [queryClient]);
  const [dragged, setDragged] = useState<string | null>(null);
  const dragId = useRef<string | null>(null);
  const [target, setTarget] = useState<Target | null>(null);
  const targetRef = useRef<Target | null>(null);
  const [moving, setMoving] = useState<Issue | null>(null);
  const [moveDestination, setMoveDestination] = useState('');
  const boardRef = useRef<HTMLDivElement>(null);
  const scrollSpeed = useRef(0);
  const visible = new Map(issues.map(p => [p.id, p]));

  useEffect(() => {
    if (!dragged) return;
    let frame: number;
    const scroll = () => { if (boardRef.current) boardRef.current.scrollLeft += scrollSpeed.current; frame = requestAnimationFrame(scroll); };
    frame = requestAnimationFrame(scroll);
    return () => cancelAnimationFrame(frame);
  }, [dragged]);

  function endDrag() { dragId.current = null; setDragged(null); setTarget(null); targetRef.current = null; scrollSpeed.current = 0; }
  async function move(id: string, next: Target, dropped = false) {
    const issue = currentIssues.current.find(issue => issue.id === id);
    const column = columns.find(column => column.id === next.columnId);
    if (!issue || !column || readOnly || pending.current) return false;
    const previousNext = order[order.indexOf(id)+1];
    const previousStatus = issue.status;
    // The ending (delight-flows `board`, Emily 2026-11-01): only a single drag into
    // Verified. The Move to column dialog can target a hidden lane with no card to settle.
    const ending = dropped && column.status === 'verified' && previousStatus !== 'verified';
    endDrag();setSettling(null);
    if(activeToast.current)toast.dismiss(activeToast.current);
    pending.current=true;setBusy(true);
    let saved: Ticket;
    try {
      // The same authenticated, optimistic PATCH as the table (useInlineIssueUpdate).
      // Wait for its response before remembering order or offering Undo. The
      // refetches it starts (issues, project summary, dashboard summary) run
      // behind the board: waiting on them too held a Manila teammate to one
      // drop every 2.4 s on 2026-10-23 (IssueBoard.dropSettle.test.tsx).
      saved = issue.status === column.status ? issue : await update.mutateAsync({id,data:{status:column.status}}) as Ticket;
      place(id,next.beforeId,currentIssues.current.map(issue=>issue.id));
    } catch {
      // The shared mutation shows the refusal. The refetch also reconciles a
      // lost response after the server saved, so retry starts from real data.
      await refresh();
      return false;
    } finally {
      pending.current=false;if(mounted.current)setBusy(false);
    }
    // Offer Undo only once the pending guard is clear, so the first click works.
    if (!mounted.current) return true;
    if (ending) {
      const reduced = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!reduced) setSettling(id);
      capture('issue_verified_moment_shown', {surface: 'dashboard', motion: reduced ? 'reduced' : 'settle'});
    }
    activeToast.current=`${toastId}-${++moveSerial.current}`;
    toast.success(ending ? `${issue.displayId ?? 'Issue'} verified.` : `${issue.displayId ?? 'Issue'} moved to ${column.label}${visibleColumns.includes(column.id) ? '' : ' (hidden column)'}`, {
      id:activeToast.current, duration:10000, action:{label:'Undo',onClick:event=>{
        event.preventDefault();
        void undoMove(saved,previousStatus,previousNext);
      }},
    });
    return true;
  }
  async function undoMove(saved: Ticket, previousStatus: TicketStatus, beforeId?: string) {
    if (pending.current || readOnly) return;
    setSettling(null);
    const id=activeToast.current ?? toastId;
    pending.current=true;setBusy(true);
    try {
      const token=await getToken();
      if(!token)throw new Error('Not authenticated');
      const latest=await ticketsApi.get(token,saved.id);
      if(latest.projectId!==projectId || latest.status!==saved.status || String(latest.updatedAt)!==String(saved.updatedAt)) {
        toast.info('This issue changed since the move. Undo is no longer available.',{id,action:undefined});return;
      }
      if(latest.status!==previousStatus)await update.mutateAsync({id:saved.id,data:{status:previousStatus}});
      place(saved.id,beforeId,currentIssues.current.map(issue=>issue.id));
      if(mounted.current)toast.success('Change undone',{id,action:undefined});
    } catch(error) {
      toast.error(toUserFacingMessage(error,'Could not undo this move. Try again.'),{id,action:undefined});
    } finally {await refresh();pending.current=false;if(mounted.current)setBusy(false);}
  }
  function hideColumn(id:string){
    if(setVisible(visibleColumns.filter(status=>status!==id))){
      // The hidden header unmounts; restore focus to the control that can reveal it.
      requestAnimationFrame(()=>document.querySelector<HTMLButtonElement>('[data-columns-picker] button')?.focus());
    }
  }
  function over(event: DragEvent, column: Column) {
    if (!dragId.current || pending.current) return;
    event.preventDefault(); event.dataTransfer.dropEffect = 'move';
    if ((event.target as HTMLElement).closest('.issue-board-drop-marker')) return;
    const node = (event.target as HTMLElement).closest<HTMLElement>('[data-issue]');
    let beforeId: string | undefined;
    if (node) {
      const id = node.dataset.issue!;
      if (id === dragId.current) return;
      const rect = node.getBoundingClientRect();
      const cards = column.cards.filter(id => id !== dragId.current && visible.has(id));
      beforeId = event.clientY < rect.top + rect.height / 2 ? id : cards[cards.indexOf(id) + 1];
    }
    targetRef.current = {columnId: column.id, beforeId};
    setTarget(targetRef.current);
  }
  return <>
    <div className={`issue-board ${dragged ? 'issue-board--dragging' : ''}`} ref={boardRef} aria-label="Issue board"
      onDragOver={e => {
        if (!dragId.current || !boardRef.current) return;
        const rect = boardRef.current.getBoundingClientRect();
        const edge = Math.min(80, rect.width / 5);
        scrollSpeed.current = e.clientX < rect.left + edge ? -(rect.left + edge - e.clientX) / 5 : e.clientX > rect.right - edge ? (e.clientX - rect.right + edge) / 5 : 0;
      }} onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) scrollSpeed.current = 0; }}>
      {columns.filter(column=>visibleColumns.includes(column.id)).map(column => {
        const cards = column.cards.flatMap(id => visible.has(id) ? [visible.get(id)!] : []);
        return <IssueBoardLane key={column.id} column={column} count={cards.length} over={target?.columnId === column.id}
          actions={[{label:'Hide column',onClick:c=>hideColumn(c.id),disabled:()=>visibleColumns.length===1,disabledTitle:'Keep at least one column visible.'}]}
          empty={cards.length ? undefined : dragged ? 'Drop card here' : 'No issues'}
          onDragOver={e => over(e, column)} onDrop={e => { e.preventDefault(); if (dragId.current) void move(dragId.current, targetRef.current?.columnId === column.id ? targetRef.current : {columnId: column.id}, true); }}>
            {cards.map(issue => <React.Fragment key={issue.id}>
              {target?.columnId === column.id && target.beforeId === issue.id && <div className="issue-board-drop-marker" role="status">Place card here</div>}
              <div className={`issue-board-issue ${dragged === issue.id ? 'issue-board-issue--dragging' : ''} ${settling === issue.id ? 'issue-board-issue--settle' : ''}`} data-issue={issue.id}
                onAnimationEnd={settling === issue.id ? event => {if (event.animationName === SETTLE_ANIMATION) setSettling(null);} : undefined}>
                <div className="issue-board-card-grip" draggable={!readOnly && !busy}
                  onDragStart={e => {
                    if (readOnly || pending.current || (e.target as HTMLElement).closest('button, input, [role="menu"]')) { e.preventDefault(); return; }
                    e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', issue.id); e.dataTransfer.setDragImage(e.currentTarget, e.clientX - e.currentTarget.getBoundingClientRect().left, e.clientY - e.currentTarget.getBoundingClientRect().top);
                    dragId.current = issue.id; setDragged(issue.id);
                  }} onDragEnd={endDrag}>
                  {renderIssue(issue, p => {setMoving(p);setMoveDestination(column.id);},busy)}
                </div>
              </div>
            </React.Fragment>)}
            {target?.columnId === column.id && !target.beforeId && <div className="issue-board-drop-marker" role="status">Place card here</div>}
        </IssueBoardLane>;
      })}
    </div>
    {busy && <p role="status" className="text-xs text-gray-500 mt-1">Saving issue status...</p>}
    <ConfirmDialog open={!!moving} title={`Move ${moving?.title ?? 'issue'}`} confirmLabel="Move issue" onCancel={() => {if(!pending.current)setMoving(null);}}
      loading={busy} confirmDisabled={!moveDestination || currentIssues.current.find(issue=>issue.id===moving?.id)?.status === moveDestination}
      onConfirm={() => {if(moving)void move(moving.id,{columnId:moveDestination}).then(saved=>{if(saved && mounted.current)setMoving(null);});}}>
      <div className="mb-5"><label className="field-label" htmlFor="move-card-column">Move to column</label><SelectDropdown id="move-card-column" aria-label="Destination column" disabled={busy} value={moveDestination} onChange={setMoveDestination} options={columns.map(c => ({value:c.id,label:c.label+(visibleColumns.includes(c.id)?'':' (hidden)')}))}/></div>
    </ConfirmDialog>
  </>;
}
